import { PageHeading } from "@via/components/page-heading.tsx";
import CloudDataConnector from "@via/pages/data/data-connections/data-source-connections/cloud-data-sources/cloud-data-connector.tsx";
import SshDataConnector from "@via/pages/data/data-connections/data-source-connections/ssh-data-source/ssh-data-connector.tsx";
import { DATA_SOURCES, OAUTH_DATA_SOURCES } from "@via/utils/constants.tsx";
import { classNames } from "@via/utils/helpers.tsx";
import { IDataSources } from "@via/utils/interfaces.tsx";
import { JSX, useState, useEffect } from "react";
import { TokenBasedDataSourceConnector } from "@via/pages/data/data-connections/data-source-connections/token-based-data-sources/token-based-data-source-connector.tsx";
import AmazonS3Connector from "@via/pages/data/data-connections/data-source-connections/amazon-s3-connector.tsx";
import GoogleCloudConnector from "@via/pages/data/data-connections/data-source-connections/google-cloud-connector.tsx";
import {
  ArchiveBoxIcon,
  CloudIcon,
  GlobeAltIcon,
  ServerStackIcon,
} from "@heroicons/react/24/outline";
import RemoteStorageConnector from "@via/pages/data/data-sources/remote-storage/remote-storage-connector.tsx";
import OAuthStorageConnector from "@via/pages/data/data-sources/remote-storage/oauth-storage-connector.tsx";
import { useOAuthConnections } from "@via/pages/data/data-sources/remote-storage/remote-storage-oauth.api.tsx";
import GeoConnector from "@via/pages/data/data-connections/data-source-connections/geo-connector.tsx";
import UrlConnector from "@via/pages/data/data-connections/data-source-connections/url-connector.tsx";
import BookmarkList from "@via/pages/data/data-connections/bookmarks/bookmark-list.tsx";
import { useSearchParams } from "react-router-dom";
import { useGlobal } from "@via/hooks/useGlobal.tsx";
import MissingDatasetIdError from "@via/pages/data/data-connections/missing-dataset-id-error.tsx";

const S3_BUCKET = "S3 Bucket";

export default function CreateDataSource(): JSX.Element {
  const [searchParams] = useSearchParams();
  const { setSelectedCollectionId, setIsSelectedCollectionPremium } =
    useGlobal();
  const [selectedSource, setSelectedSource] = useState<IDataSources>(
    DATA_SOURCES[0]
  );
  // data-2-1: a Google Drive or Dropbox tile appears only when this installation offers it.
  const { data: oauth } = useOAuthConnections();
  const sources = [
    ...DATA_SOURCES,
    ...OAUTH_DATA_SOURCES.filter(source =>
      source.id === "GoogleDrive"
        ? oauth?.providers.drive
        : oauth?.providers.dropbox
    ),
  ];

  // Validate datasetId is present - block deprecated route
  const datasetId = searchParams.get("datasetId");
  if (!datasetId) {
    return <MissingDatasetIdError />;
  }

  // Read datasetId from URL and set in context
  useEffect(() => {
    if (datasetId) {
      setSelectedCollectionId(datasetId);
      setIsSelectedCollectionPremium(true);
    }
  }, [datasetId, setSelectedCollectionId, setIsSelectedCollectionPremium]);

  const shrinkIcon = (name: string): boolean => name === S3_BUCKET;

  const handleSourceClick = (source: IDataSources) => {
    setSelectedSource(source);
  };

  const connectorComponents = {
    "cloud-storage": <CloudDataConnector source={selectedSource} />,
    "ssh-data": <SshDataConnector />,
    "token-based": <TokenBasedDataSourceConnector source={selectedSource} />,
    "remote-storage": <RemoteStorageConnector />,
    "remote-storage-oauth": (
      <OAuthStorageConnector
        key={selectedSource.id}
        provider={selectedSource.id === "GoogleDrive" ? "drive" : "dropbox"}
      />
    ),
  };

  const ConnectorComponent =
    selectedSource.id === "Amazon" ? (
      <AmazonS3Connector />
    ) : selectedSource.id === "Google" ? (
      <GoogleCloudConnector />
    ) : selectedSource.id === "Geo" ? (
      <GeoConnector />
    ) : selectedSource.id === "Url" ? (
      <UrlConnector />
    ) : (
      connectorComponents[selectedSource.category] || null
    );

  return (
    <div className="w-full">
      <div className="grid grid-cols-[250px_1fr] gap-4 h-full">
        <div className="space-y-2 border-r border-gray-300 pr-2 py-2">
          <BookmarkList />
        </div>

        <div className="p-2">
          <div className="via-page-heading-wrapper flex justify-center mb-5">
            <PageHeading title="Connect Data Source" />
          </div>
          <div>
            <label className="mb-4 block text-center text-sm font-medium leading-6 text-gray-900">
              Select the type of data source you want to connect to{" "}
            </label>
          </div>

          <div className="flex justify-center flex-wrap gap-4">
            {sources.map(source => (
              <button
                data-testid={`connectDataSource${source.name}`}
                key={source.name}
                onClick={() => handleSourceClick(source)}>
                <div
                  key={source.name}
                  className={classNames(
                    source.name === selectedSource.name ? "shadow-md" : "",
                    "flex min-h-[100px] w-28 cursor-pointer flex-col items-center justify-between rounded-md p-2 text-center duration-150 ease-in-out hover:shadow-md hover:scale-105"
                  )}>
                  {source.id === "Url" ? (
                    <GlobeAltIcon className="w-10 h-10" />
                  ) : source.id === "RemoteServer" ? (
                    <ServerStackIcon className="w-10 h-10" />
                  ) : source.id === "GoogleDrive" ? (
                    <CloudIcon className="w-10 h-10" />
                  ) : source.id === "Dropbox" ? (
                    <ArchiveBoxIcon className="w-10 h-10" />
                  ) : (
                    <img
                      alt={source.alt}
                      className={classNames(
                        shrinkIcon(source.name) ? "w-10" : "w-10 py-2"
                      )}
                      src={source.icon}
                    />
                  )}

                  <span className="text-sm">{source.name}</span>
                </div>
              </button>
            ))}
          </div>

          {ConnectorComponent}
        </div>
      </div>
    </div>
  );
}
